import BusinessIcon from "@mui/icons-material/Business";
import FactoryIcon from "@mui/icons-material/Factory";
import CategoryIcon from "@mui/icons-material/Category";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import NumbersIcon from "@mui/icons-material/Numbers";
import PhoneIcon from "@mui/icons-material/Phone";

import LocationCityIcon from "@mui/icons-material/LocationCity";
import MapIcon from "@mui/icons-material/Map";
import PublicIcon from "@mui/icons-material/Public";

import LanguageIcon from "@mui/icons-material/Language";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import { Box, Typography } from "@mui/material";
import { US_STATES } from "../../utils/States";
import { getSectorDisplayParts } from "../../constants/sectors";
import {
  normalizeCountry,
  normalizeState,
} from "../../utils/filterNormalization";
import { formatPhone } from "../../utils/Phone";

const FIFTY_STATE_CODES = new Set(US_STATES.filter((state) => state.code !== "DC").map((state) => state.code));
const FIFTY_STATE_NAMES = new Set(
  US_STATES.filter((state) => state.code !== "DC").map((state) => state.name),
);
const shouldShowUsCountryCode = (company = {}) =>
  Boolean(company.phone_number) &&
  !(
    normalizeCountry(company.country) &&
    normalizeCountry(company.country) !== "United States"
  ) &&
  (
    FIFTY_STATE_CODES.has(String(company.state || "").trim().toUpperCase()) ||
    FIFTY_STATE_NAMES.has(normalizeState(company.state))
  );

export function companyProfileConfig(company = {}) {
  // ✅ Support NAICS from multiple modules
  const naicsTextValue =
    company.naics_name || // Research (Sophie)
    company.naics_text || // Prospecting
    company.industry || // BR&E / internal
    company.naics || // Raw Dynamics
    null;
  const naicsCodeValue = company.naics_code || company.naicscode || null;
  const phoneValue = company.phone || company.phone_number || null;
  const sectorValue = company.broad_sector || company.sector || null;
  const sectorDisplay = getSectorDisplayParts(sectorValue);

  return [
    {
      title: "Company Information",
      rows: [
        {
          icon: <BusinessIcon />,
          label: "Company Name",
          value: company.name,
        },
        {
          icon: <FactoryIcon />,
          label: "Sector",
          value: sectorDisplay.standardizedSector ? (
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {sectorDisplay.standardizedSector}
              </Typography>
              {sectorDisplay.showRawSector && (
                <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                  {sectorDisplay.rawSector}
                </Typography>
              )}
            </Box>
          ) : null,
          showIf: Boolean(sectorValue),
        },
        {
          icon: <FormatListBulletedIcon />,
          label: "NAICS Text",
          value: naicsTextValue,
          showIf: Boolean(naicsTextValue),
        },
        {
          icon: <NumbersIcon />,
          label: "NAICS Code",
          value: naicsCodeValue,
          showIf: Boolean(naicsCodeValue),
        },
        {
          icon: <PhoneIcon />,
          label: "Phone",
          value: phoneValue
            ? formatPhone(phoneValue, {
                includeCountryCode: shouldShowUsCountryCode(company),
              })
            : null,
          showIf: Boolean(phoneValue),
        },
        {
          icon: <PeopleIcon />,
          label: "Employees",
          value: company.number_of_employees || company.numberofemployees,
          showIf: Boolean(
            company.number_of_employees || company.numberofemployees,
          ),
        },
        {
          icon: <CategoryIcon />,
          label: "Industries Served",
          value: company.industries_served,
          showIf: Boolean(company.industries_served),
        },
        {
          icon: <PublicIcon />,
          label: "Geographic Markets Covered",
          value: company.geographic_markets_covered,
          showIf: Boolean(company.geographic_markets_covered),
        },
        {
          icon: <LanguageIcon />,
          label: "Website",
          value: company.website ? (
            <a
              href={
                company.website.startsWith("http")
                  ? company.website
                  : `https://${company.website}`
              }
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#00336C",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {company.website}
            </a>
          ) : null,
          showIf: Boolean(company.website),
        },
        {
          icon: <DescriptionIcon />,
          label: "Description",
          value: company.description,
          showIf: Boolean(company.description),
        },
        {
          icon: <EventIcon />,
          label: "Lease Expiration",
          value: company.lease_expiration,
          showIf: Boolean(company.lease_expiration),
        },
      ],
    },

    {
      title: "Location",
      rows: [
        {
          icon: <PublicIcon />,
          label: "Country",
          value: company.country,
          showIf: Boolean(company.country),
        },
        {
          icon: <MapIcon />,
          label: "State / Province",
          value: company.state,
          showIf: Boolean(company.state),
        },
        {
          icon: <LocationCityIcon />,
          label: "City",
          value: company.city,
          showIf: Boolean(company.city),
        },
        {
          icon: <MapIcon />,
          label: "County",
          value: company.county,
          showIf: Boolean(company.county),
        },
      ],
    },
  ];
}
